import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import Layout from '@/layout/Layout.vue';
import Dashboard from '@/modules/dashboard/views/DashboardView.vue';
import LoginCallback from '@/views/LoginCallback.vue';
import { useAuthStore } from '@/stores/useAuthStore';
import { usePermissionStore } from '@/stores/usePermissionStore';
import { useFeatureModulesStore } from '@/stores/useFeatureModulesStore';
import { checkRoutePermission, getRoutePermission } from './permissionRoutes';
import { setupChunkLoadRecovery } from '@/utils/chunkLoadRecovery';
import { featureModuleForRoute } from '@/utils/featureModuleRoutes';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/auth/wecom/callback',
    name: 'WeComCallback',
    component: LoginCallback,
    meta: { requiresAuth: false },
  },
  {
    path: '/auth/feishu/callback',
    name: 'FeishuCallback',
    component: LoginCallback,
    meta: { requiresAuth: false },
  },
  {
    path: '/auth/gateway',
    name: 'AuthGateway',
    component: () => import('@/views/AuthGateway.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/share/calculator/:token',
    name: 'ShareCalculator',
    component: () => import('@/views/ShareCalculatorView.vue'),
    meta: { requiresAuth: false, title: '计算器分享' },
  },
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard',
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: Dashboard,
        meta: { requiresAuth: true, title: '项目总览' },
      },
      {
        path: 'knowledge-space',
        name: 'KnowledgeSpace',
        component: () => import('@/views/KnowledgeSpaceView.vue'),
        meta: { requiresAuth: true, title: '知识管理' },
      },
      {
        path: 'projects/create',
        name: 'ProjectCreate',
        component: () => import('@/views/ProjectCreate.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'account-merge',
        name: 'AccountMerge',
        component: () => import('@/views/AccountMergeView.vue'),
        meta: { requiresAuth: true, title: '账号合并' },
      },
      {
        path: 'project/:id/dashboard',
        name: 'ProjectDashboard',
        component: () => import('@/views/ProjectDashboard.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'project/:id/documents/:objectKey',
        name: 'ProjectDocumentViewer',
        component: () => import('@/views/ProjectDocumentViewerView.vue'),
        meta: { requiresAuth: true, title: '文档查看' },
      },
      {
        path: 'smart-document',
        name: 'SmartDocumentWorkbenchHome',
        component: () => import('@/views/smart-document/SmartDocumentWorkbenchView.vue'),
        meta: {
          requiresAuth: true,
          title: '智能文档',
          breadcrumb: [
            { label: '项目总览', to: { name: 'Dashboard' } },
            { label: '智能文档' },
          ],
        },
      },
      {
        path: 'project/:id/smart-document',
        name: 'SmartDocumentWorkbench',
        component: () => import('@/views/smart-document/SmartDocumentWorkbenchView.vue'),
        meta: {
          requiresAuth: true,
          title: '智能文档',
          breadcrumb: [
            { label: '项目总览', to: { name: 'Dashboard' } },
            { label: '{projectName}', to: { name: 'ProjectDashboard', params: { id: '{id}' } } },
            { label: '智能文档' },
          ],
        },
      },
      {
        path: 'requirements',
        name: 'Requirements',
        component: () => import('@/views/RequirementListView.vue'),
        meta: { requiresAuth: true, title: '需求台账' },
      },
      {
        path: 'project/:id/requirements',
        name: 'ProjectRequirements',
        component: () => import('@/views/ProjectRequirementLedgerView.vue'),
        meta: {
          requiresAuth: true,
          title: '需求台账',
          breadcrumb: [
            { label: '项目总览', to: { name: 'Dashboard' } },
            { label: '{projectName}', to: { name: 'ProjectDashboard', params: { id: '{id}' } } },
            { label: '需求台账' },
          ],
        },
      },
      {
        path: 'project/:id/wbs',
        name: 'ProjectWbsSnapshot',
        component: () => import('@/views/ProjectWbsSnapshotView.vue'),
        meta: {
          requiresAuth: true,
          title: 'WBS 快照',
          breadcrumb: [
            { label: '项目总览', to: { name: 'Dashboard' } },
            { label: '{projectName}', to: { name: 'ProjectDashboard', params: { id: '{id}' } } },
            { label: 'WBS 快照' },
          ],
        },
      },
      {
        path: 'designs',
        name: 'Designs',
        component: () => import('@/views/DesignListView.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'resources',
        name: 'Resources',
        component: () => import('@/views/ResourceLibraryListView.vue'),
        meta: {
          requiresAuth: true,
          title: '通用部件库',
          breadcrumb: [
            { label: 'AI工具集', to: { name: 'AiTools' } },
            { label: '通用部件库' },
          ],
        },
      },
      {
        path: 'resources/create',
        name: 'ResourceCreate',
        component: () => import('@/views/ResourceCreateView.vue'),
        meta: {
          requiresAuth: true,
          title: '新建部件',
          breadcrumb: [
            { label: 'AI工具集', to: { name: 'AiTools' } },
            { label: '通用部件库', to: { name: 'Resources' } },
            { label: '新建部件' },
          ],
          showInMenu: false,
        },
      },
      {
        path: 'resources/:id',
        name: 'ResourceDetail',
        component: () => import('@/views/ResourceListView.vue'),
        meta: {
          requiresAuth: true,
          title: '部件详情',
          breadcrumb: [
            { label: 'AI工具集', to: { name: 'AiTools' } },
            { label: '通用部件库', to: { name: 'Resources' } },
            { label: '{componentName}' },
          ],
        },
      },
      {
        path: 'ai-tools',
        name: 'AiTools',
        component: () => import('@/views/AiToolsView.vue'),
        meta: { requiresAuth: true, title: 'AI工具集' },
      },
      {
        path: 'ai-requirements',
        name: 'AiRequirementLedger',
        component: () => import('@/views/AiRequirementLedgerView.vue'),
        meta: {
          requiresAuth: true,
          title: 'AI需求台账',
          breadcrumb: [
            { label: 'AI工具集', to: { name: 'AiTools' } },
            { label: 'AI需求台账' },
          ],
        },
      },
      {
        path: 'ai-requirements/:id',
        name: 'AiRequirementDetail',
        component: () => import('@/views/AiRequirementDetailView.vue'),
        meta: {
          requiresAuth: true,
          title: '需求详情',
          breadcrumb: [
            { label: 'AI工具集', to: { name: 'AiTools' } },
            { label: 'AI需求台账', to: { name: 'AiRequirementLedger' } },
            { label: '需求详情' },
          ],
        },
      },
      {
        path: 'ai-review',
        name: 'AiSmartReview',
        component: () => import('@/views/AiSmartReviewView.vue'),
        meta: {
          requiresAuth: true,
          title: 'AI智审',
          breadcrumb: [
            { label: 'AI工具集', to: { name: 'AiTools' } },
            { label: 'AI智审' },
          ],
        },
      },
      {
        path: 'ai-calculator',
        name: 'AiCalculator',
        component: () => import('@/views/AiCalculatorView.vue'),
        meta: {
          requiresAuth: true,
          title: 'AI计算器',
          breadcrumb: [
            { label: 'AI工具集', to: { name: 'AiTools' } },
            { label: 'AI计算器' },
          ],
        },
      },
      {
        path: 'ai-calculator/formula-share/:publicId',
        name: 'AiCalculatorFormulaShare',
        component: () => import('@/views/AiCalculatorFormulaShareView.vue'),
        meta: {
          requiresAuth: true,
          title: '公式分享',
          breadcrumb: [
            { label: 'AI工具集', to: { name: 'AiTools' } },
            { label: 'AI计算器', to: { name: 'AiCalculator' } },
            { label: '公式分享' },
          ],
        },
      },
      {
        path: 'ai-calculator/:formulaId',
        name: 'AiCalculatorDetail',
        component: () => import('@/views/AiCalculatorDetailView.vue'),
        meta: {
          requiresAuth: true,
          title: '计算器',
          breadcrumb: [
            { label: 'AI工具集', to: { name: 'AiTools' } },
            { label: 'AI计算器', to: { name: 'AiCalculator' } },
            { label: '{formulaName}' },
          ],
        },
      },
      {
        path: 'ai-3d-viewer',
        name: 'Ai3DViewer',
        component: () => import('@/views/Ai3DViewerView.vue'),
        meta: {
          requiresAuth: true,
          title: '3D Viewer',
          breadcrumb: [
            { label: 'AI工具集', to: { name: 'AiTools' } },
            { label: '3D Viewer' },
          ],
        },
      },
      {
        path: 'ai-3d-viewer/offline/:jobId',
        name: 'Ai3DViewerOfflinePreview',
        component: () => import('@/views/Ai3DViewerOfflinePreviewView.vue'),
        meta: {
          requiresAuth: true,
          title: '离线预览',
          breadcrumb: [
            { label: 'AI工具集', to: { name: 'AiTools' } },
            { label: '3D Viewer', to: { name: 'Ai3DViewer' } },
            { label: '{offlinePreviewStem}预览图' },
          ],
        },
      },
      {
        path: 'ai-3d-viewer/share/:publicId',
        name: 'Ai3DViewerSharePreview',
        component: () => import('@/views/Ai3DViewerSharePreviewView.vue'),
        meta: {
          requiresAuth: true,
          title: '分享的离线预览',
          breadcrumb: [
            { label: 'AI工具集', to: { name: 'AiTools' } },
            { label: '3D Viewer', to: { name: 'Ai3DViewer' } },
            { label: '{offlinePreviewStem}预览图' },
          ],
        },
      },
      {
        path: 'notifications',
        name: 'Notifications',
        component: () => import('@/views/NotificationCenterView.vue'),
        meta: { requiresAuth: true, title: '消息中心' },
      },
      {
        path: 'org/chart',
        name: 'OrgChart',
        component: () => import('@/views/OrgChart.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'org/users',
        name: 'UserManagement',
        component: () => import('@/views/UserManagement.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'org/departments',
        name: 'DepartmentManagement',
        component: () => import('@/views/DepartmentManagement.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'org/sync',
        name: 'SyncManagement',
        component: () => import('@/views/SyncManagement.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'org/mapping',
        name: 'PositionMapping',
        component: () => import('@/views/PositionMapping.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'org/user-roles',
        name: 'UserRoleAssignment',
        component: () => import('@/views/UserRoleAssignment.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'reports/approval',
        name: 'ReportApproval',
        component: () => import('@/views/ReportApproval.vue'),
        meta: {
          requiresAuth: true,
          title: '报告审批',
          menuOrder: 50,
        },
      },
      {
        path: 'debug',
        name: 'DebugDashboard',
        component: () => import('@/views/DebugDashboard.vue'),
        meta: { requiresAuth: true, showInMenu: false },
      },
      {
        path: 'help',
        name: 'HelpManual',
        component: () => import('@/views/HelpManualView.vue'),
        meta: { requiresAuth: true, title: '产品使用手册', showInMenu: false },
      },
      {
        path: 'user/profile',
        name: 'UserProfile',
        component: () => import('@/views/UserProfile.vue'),
        meta: { requiresAuth: true, showInMenu: false },
      },
      {
        path: 'admin/org',
        name: 'OrgAdmin',
        component: () => import('@/views/admin/org/OrgAdminView.vue'),
        meta: {
          requiresAuth: true,
          title: '组织架构',
          menuOrder: 90,
          showInMenu: false,
        },
      },
      {
        path: 'admin/department-mapping',
        name: 'DepartmentRoleMapping',
        redirect: '/admin/org',
        meta: {
          requiresAuth: true,
          title: '部门角色映射',
          menuOrder: 90,
        },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

setupChunkLoadRecovery(router);

// 路由守卫
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  const permissionStore = usePermissionStore();
  const featureModulesStore = useFeatureModulesStore();
  const UNCONFIGURED_ALLOW_ROUTES = new Set<string>([
    'AiTools',
    'AiRequirementLedger',
    'AiRequirementDetail',
    'AiSmartReview',
    'AiCalculator',
    'AiCalculatorDetail',
    'Ai3DViewer',
    'Ai3DViewerOfflinePreview',
    'Ai3DViewerSharePreview',
    'UserProfile',
    'Notifications',
    'AccountMerge',
    'HelpManual',
    'Dashboard',
    'KnowledgeSpace',
  ]);
  
  // 检查路由是否需要认证
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth !== false);

  // 功能开关对登录页也生效（如分享页路由）；尽早拉取一次
  if (!featureModulesStore.loaded) {
    await featureModulesStore.fetchFeatures();
  }
  
  if (requiresAuth) {
    // 检查是否已认证
    if (!authStore.isAuthenticated) {
      // 未认证，跳转到登录页，并保存目标路由
      next({
        path: '/login',
        query: { redirect: to.fullPath },
      });
      return;
    }
    
    // 已认证，获取权限（如果还没有）
    if (permissionStore.permissions.length === 0) {
      await permissionStore.fetchPermissions();
    }

    // 功能模块关闭时拦截对应路由
    const featureKey = featureModuleForRoute(to.name);
    if (featureKey && !featureModulesStore.enabled(featureKey)) {
      next({ name: featureModulesStore.fallbackRouteName() });
      return;
    }

    // 未配置角色：仅开放 AI 工具集与 AI 对话相关入口（项目相关功能全部禁用）
    // 飞书工作台落地页登录后，角色可能仅在 authStore（JWT/登录响应）中，权限接口尚未写入 permissionStore，需合并判断避免误判闪跳
    const roleCount = Math.max(
      permissionStore.roles?.length ?? 0,
      authStore.roles?.length ?? 0,
    );
    const isUnconfigured = roleCount === 0;
    if (isUnconfigured && to.name && typeof to.name === 'string') {
      if (!UNCONFIGURED_ALLOW_ROUTES.has(to.name)) {
        const fallback = featureModulesStore.aiToolsEnabled
          ? 'AiTools'
          : featureModulesStore.fallbackRouteName();
        next({ name: fallback });
        return;
      }
      // 未配置角色且目标 AI 工具已关闭时，落到可用首页
      if (
        (to.name === 'AiTools' || String(to.name).startsWith('Ai'))
        && !featureModulesStore.aiToolsEnabled
      ) {
        next({ name: featureModulesStore.fallbackRouteName() });
        return;
      }
    }
    
    // 检查路由权限
    if (to.name && typeof to.name === 'string') {
      const hasPermission = (codes: string | string[], requireAll = false) => {
        return permissionStore.hasPermissions(codes, requireAll);
      };
      
      const hasRole = (codes: string | string[], requireAll = false) => {
        return permissionStore.hasRoles(codes, requireAll);
      };

      const hasMatrix = (codes: string | string[], requireAll = false) => {
        const list = Array.isArray(codes) ? codes : [codes];
        return requireAll
          ? list.every((c) => permissionStore.checkMatrixPermission(c))
          : list.some((c) => permissionStore.checkMatrixPermission(c));
      };
      
      const hasAccess = checkRoutePermission(to.name, hasPermission, hasRole, hasMatrix);
      
      if (!hasAccess) {
        // 无权限，获取重定向路径
        const permissionConfig = getRoutePermission(to.name);
        const redirectPath = permissionConfig?.redirect || '/dashboard';
        
        // 可以显示提示信息
        console.warn(`无权限访问路由: ${to.name}`);
        
        next(redirectPath);
        return;
      }
    }
    
    // 已认证，检查Token是否即将过期，尝试刷新
    if (authStore.refreshToken) {
      // 这里可以添加Token过期检查逻辑
      // 暂时直接通过
    }
  } else {
    // 不需要认证的路由
    // 如果已登录，访问登录页则跳转到首页
    if (to.path === '/login' && authStore.isAuthenticated) {
      next('/dashboard');
      return;
    }
  }
  
  next();
});

export default router;
